import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';

export interface CabeceraDTO {
  id: string;
  project_id: string;
  nombre: string;
  created_at: string;
  updated_at: string;
}

// ── List ──

export function useCabeceras(projectId: string) {
  return useQuery<CabeceraDTO[]>({
    queryKey: queryKeys.cabeceras.byProject(projectId),
    queryFn: () => api.get<CabeceraDTO[]>(`/projects/${projectId}/cabeceras`).then((r) => r.data),
    enabled: !!projectId,
  });
}

// ── Create ──

export function useCreateCabecera(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { nombre: string }) =>
      api.post<CabeceraDTO>(`/projects/${projectId}/cabeceras`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cabeceras.byProject(projectId) });
      toast('success', 'Cabecera creada');
    },
    onError: () => {
      toast('error', 'Error al crear cabecera');
    },
  });
}

// ── Update ──

export function useUpdateCabecera(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cabeceraId, nombre }: { cabeceraId: string; nombre: string }) =>
      api.put<CabeceraDTO>(`/cabeceras/${cabeceraId}`, { nombre }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cabeceras.byProject(projectId) });
      toast('success', 'Cabecera actualizada');
    },
    onError: () => {
      toast('error', 'Error al actualizar cabecera');
    },
  });
}

// ── Delete ──

export function useDeleteCabecera(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cabeceraId: string) => api.delete(`/cabeceras/${cabeceraId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.cabeceras.byProject(projectId) });
      toast('success', 'Cabecera eliminada');
    },
    onError: () => {
      toast('error', 'Error al eliminar cabecera');
    },
  });
}
